import { SelectC, TextFieldC } from 'components'
import { useLoadOrdersProductsParticularsOptions } from 'modules/orders/hooks'
import { useOrderStore } from 'modules/orders/store'
import { useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useDebounce } from 'utils'
import { useItemFormDrawerStore } from '../ItemForm/itemFormDrawerStore'
import { isPreviousFieldHasFilled } from './helpers'
import { interpretExpression } from './helpers/interpretExpression'
import { useCreateIdentifier } from './services/useCreateIdentifier'

type Props = {
  name: string
  contentType: 'L' | 'P' | 'F'
  dataType: string
  expression?: string
  configurationId?: string
  disabled?: boolean
  onChange?: (name: string) => void
  confLength: number
  previousField: string
  field: any
}

const logicalFieldOptions = [
  {
    id: 'S',
    content: 'S',
    description: 'SIM'
  },
  {
    id: 'N',
    content: 'N',
    description: 'NÃO'
  }
]

export const ConfigurationField = (props: Props) => {
  const {
    name,
    contentType,
    dataType,
    expression,
    configurationId,
    disabled,
    confLength,
    previousField,
    field,
    onChange
  } = props
  const [result, setResult] = useState(0)
  const { setValue, watch, setError, clearErrors } = useFormContext()

  const form = useOrderStore.useForm()
  const selectedItem = useItemFormDrawerStore.useSelectedItem()

  const isFirst = name === previousField

  const fields = watch()
  const { data: preConf } = useCreateIdentifier(fields)

  const isConfigurationString = !!(
    preConf && confLength === preConf?.split?.(';')?.length - 2
  )

  const confDeb = useDebounce(preConf, 500)

  const { data: ParticularsOptionsResp, isFetching } =
    useLoadOrdersProductsParticularsOptions({
      configurationId,
      particularId: contentType === 'L' ? '' : name,
      configuration: confDeb,
      productId: selectedItem?.id || selectedItem?.product?.id,
      customerId: form?.customer?.id,
      enabled:
        !disabled &&
        // (isConfigurationString || isConfigurationStringTMAN) &&
        contentType === 'P' &&
        (isFirst ? true : isPreviousFieldHasFilled(confDeb, name)) &&
        (previousField === name || (!!previousField && !!watch(previousField)))
    })

  useEffect(() => {
    if (expression && Object.keys(fields).length) {
      const result = interpretExpression(expression, fields)
      setResult(result)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expression, fields, name, setValue])

  useEffect(() => {
    if (expression) {
      const value = result.toFixed(2)
      setValue(name, value)
      setValue('quantity', value)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, setValue])

  const currentField = fields[name]

  const getLabel = useMemo(() => {
    let label = field.name

    if (
      field?.initRange != null &&
      field?.endRange != null &&
      dataType === 'N' &&
      contentType === 'F'
    )
      label += ` (${field?.initRange} - ${field?.endRange})`

    return label
  }, [field])

  useEffect(() => {
    if (dataType !== 'N' || contentType !== 'F') return
    if (field?.initRange == null || field?.endRange == null) return

    if (!currentField) {
      clearErrors(name)
      return
    }

    if (
      Number(currentField) < field?.initRange ||
      Number(currentField) > field?.endRange
    ) {
      setError(name, {
        message: `deve estar dentro da faixa de valores (${field?.initRange} - ${field?.endRange})`
      })
    } else {
      clearErrors(name)
    }
  }, [name, setError, clearErrors, field, currentField, dataType, contentType])

  useEffect(() => {
    if (ParticularsOptionsResp?.data.length === 1) {
      setValue(name, ParticularsOptionsResp?.data[0])
      onChange?.(name)
    }
  }, [ParticularsOptionsResp?.data, name, onChange, setValue])

  if (dataType === 'N' && contentType !== 'P')
    return (
      <TextFieldC
        name={name}
        label={getLabel}
        disabled={disabled}
        onBlur={() => {
          onChange?.(name)
        }}
        normalize={(text) => {
          if (name === '006') return text.replace(/\D/g, '')
          if (dataType === 'N')
            return text.replace(/\D/g, '').replace(/(\d{1,2})$/, '.$1')

          return text
        }}
      />
    )

  return (
    <SelectC
      name={name}
      label={getLabel}
      disabled={disabled}
      onChange={() => onChange?.(name)}
      loading={isFetching}
      options={
        contentType === 'L'
          ? logicalFieldOptions
          : ParticularsOptionsResp?.data || []
      }
      getOptionLabel={(option: any) =>
        option?.description?.trim() || option?.content?.trim()
      }
    />
  )
}
